"use client";

import { DayAheadTable } from "@/components/DayAheadTable";
import { KWHCounterConfig, KWHCounterGraph } from "@/components/KWHCounterGraph";
import { AnimatedThemeToggler } from "@/components/magicui/AnimatedThemeToggler";
import { DateLib, DateRange, daysInRange, formatRange, isValidRange, TimeRangeSelector, TimeRangeSelectorProps, WEEK_STARTS_ON } from "@/components/TimeRangeSelector";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { api } from "@/convex/_generated/api";
import { useRichQuery } from "@/lib/convex";
import { EllipsisVertical, Printer } from "lucide-react";
import { DateTime } from "luxon";
import React from "react";
import { useReactToPrint } from "react-to-print";
import { useLocalStorage } from "usehooks-ts";

const KWHCounters: KWHCounterConfig[] = [
  {
    name: "Totale opbrengst zonnepanelen (PAC)",
    path: "PV",
    chart: {
      categories: ["Injectie"],
      colors: ["yellow"]
    }
  },
  {
    name: "Totale opbrengst batterijen (PAC)",
    path: "BAT",
    chart: {
      categories: ["Injectie"],
      colors: ["emerald"]
    }
  },
  {
    name: "Verbruik van batterijen",
    path: "BAT",
    chart: {
      categories: ["Afname net", "Afname PV"],
      colors: ["gray", "yellow"]
    }
  },
  {
    name: "Verbruik oud gebouw",
    path: "AFS",
    noInjection: true,
    chart: {
      categories: ["Injectie", "Afname net", "Afname PV", "Afname batterijen"],
      colors: ["red", "gray", "yellow", "emerald"]
    }
  },
  {
    name: "Verbruik nieuwbouw",
    path: "NB",
    noInjection: true,
    chart: {
      categories: ["Injectie", "Afname net", "Afname PV", "Afname batterijen"],
      colors: ["red", "gray", "yellow", "emerald"]
    }
  },
]

export const QUICK_SELECTIONS = [
  {
    label: "Gisteren",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59)
      };
    }
  },
  {
    label: "Vandaag",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)
      };
    }
  },
  {
    label: "Deze week",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const sunday = dateLib.startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON });
      return {
        from: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + WEEK_STARTS_ON, 0, 0, 0),
        to: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + WEEK_STARTS_ON + 6, 23, 59, 59)
      };
    }
  },
  {
    label: "Deze maand",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)
      };
    }
  },
  {
    label: undefined
  },
  {
    label: "Laatste 3 dagen",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2, 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)
      };
    }
  },
  {
    label: "Laatste 7 dagen",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6, 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)
      };
    }
  },
  {
    label: "Laatste 30 dagen",
    value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29, 0, 0, 0),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)
      };
    }
  },
] satisfies TimeRangeSelectorProps["quickSelections"];

export default function Home() {
  const { data: firstAndLast } = useRichQuery(api.kwh.firstAndLast, {});
  const [range, setRange] = React.useState<DateRange | undefined>(() => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      to: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
    };
  });
  const maxRange = React.useMemo(() => ({
    from: DateTime.fromMillis(firstAndLast?.first.ts ?? 0).set({ hour: 0, minute: 0, second: 0 }).toJSDate(),
    to: DateTime.fromMillis(firstAndLast?.last.ts ?? Date.now()).set({ hour: 0, minute: 0, second: 0 }).toJSDate()
  }), [firstAndLast])
  const [noInjection, setNoInjection] = useLocalStorage<boolean>("hide_injection", true, { initializeWithValue: false });
  const [visibleGraphs, setVisibleGraphs] = useLocalStorage<string[]>("visible_graphs", KWHCounters.map(c => c.name), { initializeWithValue: false });

  const printRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });

  const [mounted, setMounted] = React.useState<boolean>(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return (<>
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  </>);

  return (
    <div className="">
      <div className="flex flex-wrap items-center gap-2 mb-2 sticky z-[50] top-0 bg-sidebar border-b p-2">
        <h1 className="text-2xl font-bold flex flex-row items-baseline px-4">
          Energie monitor
        </h1>

        <div className="grow"></div>

        <TimeRangeSelector className="gap-2" range={range} setRange={setRange} maxRange={maxRange} quickSelections={QUICK_SELECTIONS} />

        <Separator orientation="vertical" className="data-[orientation=vertical]:h-4" />

        <Button className="hidden sm:block" variant="ghost" onClick={reactToPrintFn}>
          <Printer />
        </Button>

        <AnimatedThemeToggler />

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="size-6">
              <EllipsisVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={10}>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex flex-row items-stretch">
                <span className="grow">Tijdselectie</span>
                <span className="ml-2 text-sm text-muted-foreground">
                  {daysInRange(range)} dagen
                </span>
              </DropdownMenuLabel>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {new Intl.DateTimeFormat("nl-BE", { dateStyle: "medium", timeStyle: "short" }).format(range?.from)}
                {" - "}
                {new Intl.DateTimeFormat("nl-BE", { dateStyle: "medium", timeStyle: "short" }).format(range?.to)}
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              <DropdownMenuLabel>Grafieken</DropdownMenuLabel>
              {
                KWHCounters.map((counter, idx) => (
                  <DropdownMenuCheckboxItem
                    key={`${counter.path}-${idx}`}
                    checked={visibleGraphs.includes(counter.name)}
                    onCheckedChange={(checked) => {
                      setVisibleGraphs((prev) => {
                        if (checked) {
                          return [...prev, counter.name];
                        } else {
                          return prev.filter((name) => name !== counter.name);
                        }
                      });
                    }}
                  >
                    {counter.name}
                  </DropdownMenuCheckboxItem>
                ))
              }
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              <DropdownMenuLabel>Opties</DropdownMenuLabel>
              <DropdownMenuCheckboxItem checked={noInjection} onCheckedChange={setNoInjection}>
                Injectie compensatie
              </DropdownMenuCheckboxItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>

      <div className="space-y-4 p-4">

        {/* <div className="border rounded-lg p-4">
          <h1 className="text-lg font-semibold">Totale periode</h1>
          {firstAndLast ? `${new Intl.DateTimeFormat("nl-BE", { dateStyle: "long", timeStyle: "short" })
            .format(DateTime.fromMillis(firstAndLast.first.ts).toJSDate())} - ${new Intl.DateTimeFormat("nl-BE", { dateStyle: "long", timeStyle: "short" })
              .format(DateTime.fromMillis(firstAndLast.last.ts).toJSDate())}` : "Onbekend"}
          <br />
          <Totalizer name="PV" field="export" />

          <Totalizer name="AFS" field="importPV" />
          <Totalizer name="AFS" field="importGrid" />

          <Totalizer name="NB" field="importPV" />
          <Totalizer name="NB" field="importGrid" />
        </div>

        <div className="border rounded-lg p-4">
          <h1 className="text-lg font-semibold">Injectie naar net</h1>
          <ExportGraph range={range} />
        </div> */}


        <div ref={printRef} className="space-y-4 print:p-4">
          <div className="hidden print:block text-right">
            {isValidRange(range) && formatRange(range)}
          </div>
          {
            KWHCounters
              .filter(c => visibleGraphs.includes(c.name))
              .map((counter, idx) => (
                <div key={counter.path + "-" + idx} className="border rounded-lg p-4 space-y-2">
                  <KWHCounterGraph path={counter.path} name={counter.name} range={range} noInjection={noInjection && counter.noInjection} config={counter.chart} />
                </div>
              ))
          }
        </div>

        <div className="border rounded-lg p-4 space-y-2">
          <h1 className="text-lg font-semibold mb-2">Dagprijzen</h1>
          <DayAheadTable range={range} />
        </div>
      </div>
    </div>
  );
}
